<script lang="ts">
import { FusionChartConfig } from '@/types/MultiSeries';
import { DoughnutData, formatDoughnutChartData, FusionChartDoughnut2D } from '@/types/Doughnut';
import { compactNumber } from '@/utilities/Number';
import { paletteColors } from '@/utilities/Color';
import { Bar2DFusionChartConfig } from '@/types/Bar';
import { MinusIcon, PlusIcon, ChevronUp, ChevronDown } from 'lucide-vue-next';
import { Store } from '@/store';

export default {
  name: 'AccountOverview',
  components: {
    PlusIcon,
    MinusIcon,
    ChevronDown,
    ChevronUp
  },
};

interface Month {
  label: string[];
  programs: Program[];
  even: boolean;
  key: string;
}

interface Sort {
  key: string;
  direction: 'ascending' | 'descending';
}

const NUM_PROGS_TO_SHOW = 4;

const chartDefault: FusionChartConfig = {
  animation: '1',
  theme: 'cacs',
  showLegend: true,
  forceYAxisValueDecimals: false,
  decimals: 1,
};

const limitByCoverageTypeChart: FusionChartConfig = {
  ...chartDefault,
  xAxisName: 'Coverage',
  exportFilename: 'Limit by Coverage',
};

const retentionByCoverageTypeChart: Bar2DFusionChartConfig = {
  ...chartDefault,
  xAxisName: 'Coverage',
  exportFilename: 'Deductible/Retention by Coverage',
};

function premiumByCoverageTypeDataSource(data: DoughnutData[]): FusionChartDoughnut2D {
  const total = compactNumber(data.reduce((acc, r) => acc + r.value, 0));
  return {
    chart: {
      ...chartDefault,
      exportFilename: 'Premium by Coverage',
      defaultCenterLabel: `Total<br><b>$${total}</b>`,
      centerLabel: '$label <b>$$value</b>',
      decimals: 0,
      paletteColors: paletteColors,
      centerLabelFontSize: 15,
      showLegend: true,
      showBorder: false,
      doughnutRadius: '55%',
    },
    data: formatDoughnutChartData(data),
  };
}

function premiumByCarrierDataSource(data: DoughnutData[]): FusionChartDoughnut2D {
  const total = compactNumber(data.reduce((acc, r) => acc + r.value, 0));

  return {
    chart: {
      ...chartDefault,
      captionFontSize: 20,
      exportFilename: 'Premium by Carrier',
      defaultCenterLabel: `Total<br><b>$${total}</b>`,
      centerLabel: '<b>$label</b> $$value',
      decimals: 0,
      captionAlignment: 'center',
      paletteColors: paletteColors,
      centerLabelFontSize: 15,
      showLegend: true,
      doughnutRadius: '55%',
    },
    data: formatDoughnutChartData(data),
  };
}
</script>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import { accountService } from '@/services/api';
import PageProgress from '@/components/PageProgress.vue';
import { barChartColors } from '@/utilities/Color';
import FilterBar from '@/components/FilterBar.vue';
import { DateTime } from 'luxon';
import { AccountContact, AccountContactRow, ProgramContact } from '@/types/AccountContact';
import { Program, ProgramSummary } from '@/types/Programs';
import { IsUserAClient, tenantName, UserHasPermission } from '@/utilities/AuthUtils';
import { compactDisplayValueForDoughnutChart } from '@/types/Doughnut';
import { BarChartData, compactDisplayValueForBarChart, FusionChartBar2D } from '@/types/Bar';
import { compactMoney } from '@/utilities/Money';
import AccountProgramContact from '@/components/AccountProgramContact.vue';
import Toggle from '@/components/v2/Toggle.vue';
import { UserPermission } from '@/types/UserPermissions.js';

const $route = useRoute();
const MAX_PROGRAM_NAME_LENGTH = 25;

// -----
// data
// -----
const loading = ref(true);
const showExternal = ref(false);
const showNonRecurring = ref(true);
const hideUnpublishedRoles = ref(true);
const premiumByCoverageData = ref([] as DoughnutData[]);
const premiumByCarrierData = ref([] as DoughnutData[]);
const limitByCoverageTypeData = ref({} as BarChartData[]);
const retentionByCoverageTypeData = ref({} as BarChartData[]);
const showAllExpirations = ref(false);
const programContacts = ref([] as ProgramContact[]);
const programContactsMap = ref(new Map<number, ProgramContact>());
const programSummary = ref([] as ProgramSummary[]);
const expirationsByMonth = ref([] as Month[]);
const expirationsByMonthShort = ref([] as Month[]);
const expirationsByMonthFull = ref([] as Month[]);
const programs = ref([] as Program[]);
const selectedBarChart = ref(0);
const selectedDoughnutChart = ref(0);
const programDetailsHeaderRef = ref<HTMLDivElement | null>(null);
const programDetailsSort = ref<Sort | undefined>(undefined);

// ---------
// computed
// ---------
const premiumByCoverageType = computed(() => premiumByCoverageTypeDataSource(premiumByCoverageData.value));
const premiumByCarrier = computed(() => premiumByCarrierDataSource(premiumByCarrierData.value));
const retentionByCoverageType = computed<FusionChartBar2D>(() => {
  return {
    chart: { ...retentionByCoverageTypeChart, paletteColors: barChartColors },
    data: retentionByCoverageTypeData.value,
  };
});

const limitByCoverageType = computed<FusionChartBar2D>(() => {
  return {
    chart: { ...limitByCoverageTypeChart, paletteColors: barChartColors },
    data: limitByCoverageTypeData.value,
  };
});

const maxNumProgramsInMonth = computed(() => {
  return expirationsByMonthFull.value.reduce((maxCount, month) => {
    const numPrograms = month.programs.length;
    return maxCount > numPrograms ? maxCount : numPrograms;
  }, 0);
});

const showExternalLabel = computed(() => {
  return tenantName() !== '' ? `Show Non-${tenantName()} Policies` : 'Show External Policies';
});

const canAccessPrograms = computed(() => {
  if (IsUserAClient(Store.state.user)) {
    return UserHasPermission(Store.state.user, UserPermission.ProgramsRead);
  }

  return true;
});

// -----------
// lifecycles
// -----------
onMounted(async () => {
  let accountInfo = await accountService.findById($route.params.id as string);
  if (accountInfo.accountStatus === 'P') {
    showExternal.value = true;
  }

  hideUnpublishedRoles.value = IsUserAClient(Store.state.user);

  await refresh();
  return;
});

// ---------
// watchers
// ---------
watch(showExternal, () => {
  refresh();
});

watch(showNonRecurring, () => {
  refresh();
});

// ----------
// functions
// ----------

async function refresh() {
  loading.value = true;

  const [accountId, now] = [$route.params.id as string, DateTime.local()];
  const startDate = now.toUTC().startOf('month');
  const endDate = startDate.plus({ years: 1, months: 1 }).startOf('month');

  const [
    premiumByCoverageTypeResult,
    premiumByCarrierResult,
    limitByCoverageTypeResult,
    retentionByCoverageTypeResult,
    activeContacts,
    programExpirations,
    programsResult,
    programSummaryResult,
  ] = await Promise.all([
    compactDisplayValueForDoughnutChart(accountService.premiumByCoverageType(Number(accountId), !showExternal.value, showNonRecurring.value)),
    compactDisplayValueForDoughnutChart(accountService.premiumByCarrier(Number(accountId), !showExternal.value, showNonRecurring.value)),
    compactDisplayValueForBarChart(accountService.maxLimitByCoverageType(accountId, showExternal.value, showNonRecurring.value)),
    compactDisplayValueForBarChart(accountService.retentionByCoverageType(accountId, showExternal.value, showNonRecurring.value)),
    accountService.getActiveContacts(accountId, showNonRecurring.value, hideUnpublishedRoles.value),
    accountService.programExpiration(accountId, startDate.toISODate(), endDate.toISODate(), !showExternal.value, showNonRecurring.value),
    accountService.getProgramsForAccount(accountId, true, !showExternal.value, showNonRecurring.value),
    accountService.getProgramSummary(accountId, !showExternal.value, showNonRecurring.value),
  ]);

  const monthMap = programExpirations.reduce((monthMap, pe) => {
    const expiresAt = DateTime.fromISO(pe.expiresAt, { zone: 'UTC' });
    const key = `${expiresAt.monthShort}-${expiresAt.year}`;

    if (!monthMap.has(key)) {
      monthMap.set(key, { key, label: [expiresAt.monthShort ?? ''], programs: [], even: false });
    }
    monthMap.get(key)?.programs.push(pe);
    return monthMap;
  }, new Map<string, Month>());

  let n = now;
  let expirations = [] as Month[];

  let year = -1;
  for (let i = 0; i < 12; i++) {
    const key = `${n.monthShort}-${n.year}`;
    const month = monthMap.get(key) || { key, label: [n.monthShort], programs: [], even: false };

    // Only 1 month of each year displays year suffix:
    // FEB 2023  MAR  APR  ...  DEC  JAN 2024
    if (year !== n.year) {
      month.label[0] += ` ${n.year}`;
      year = n.year;
    }

    month.programs.sort((p1, p2) => p1.name.localeCompare(p2.name));
    expirations.push(month);

    n = n.plus({ month: 1 });
  }

  // user can toggle "Show (More|Less)" -> store off long and short versions of data

  expirationsByMonthFull.value = [...expirations];
  expirationsByMonthShort.value = [...expirations].map((e) => {
    if (e.programs.length > NUM_PROGS_TO_SHOW) {
      return {
        ...e,
        programs: [...e.programs.slice(0, 4), { ...e.programs[NUM_PROGS_TO_SHOW], name: `${e.programs.length - NUM_PROGS_TO_SHOW} more` }],
      };
    }
    return e;
  });
  expirationsByMonth.value = expirationsByMonthShort.value;

  premiumByCoverageData.value = premiumByCoverageTypeResult;
  premiumByCarrierData.value = premiumByCarrierResult;
  limitByCoverageTypeData.value = limitByCoverageTypeResult;
  retentionByCoverageTypeData.value = retentionByCoverageTypeResult;

  /* Account contact table grouping logic */
  programs.value = programsResult;
  const programMap = new Map<number, string>();
  programs.value.forEach((e) => {
    programMap.set(e.id, e.name);
  });

  // Map Program to its Contacts
  const programToContacts = activeContacts.reduce((acc, curr) => {
    if (!programMap.has(curr.programID)) {
      return acc;
    }

    if (!acc.has(curr.programID)) {
      acc.set(curr.programID, [curr]);
    } else {
      acc.get(curr.programID)?.push(curr);
    }

    return acc;
  }, new Map<number, AccountContact[]>());

  // Format data for table
  programContacts.value = Array.from(programToContacts.entries()).map((group) => {
    const [programID, contacts] = [group[0], group[1]];
    const programName = programMap.get(programID);

    // Map role to list of contacts
    let roleToContacts = contacts.reduce((acc, curr) => {
      if (!acc.has(curr.serviceTitle)) {
        acc.set(curr.serviceTitle, [curr]);
      } else {
        acc.get(curr.serviceTitle)?.push(curr);
      }
      return acc;
    }, new Map<string, AccountContact[]>());

    // Map email to list of contacts
    let emailToContacts = contacts.reduce((acc, curr) => {
      if (!acc.has(curr.email)) {
        acc.set(curr.email, [curr]);
      } else {
        acc.get(curr.email)?.push(curr);
      }
      return acc;
    }, new Map<string, AccountContact[]>());

    let accountContactRows: AccountContactRow[] = [];

    //1 contact to multiple roles
    for (const [, contacts] of emailToContacts.entries()) {
      let accountContactRow = new AccountContactRow();

      // if a contact has more than 1 role
      if (contacts.length > 1) {
        contacts.forEach((contact) => {
          let len = roleToContacts.get(contact.serviceTitle)?.length;
          if (len && len === 1) {
            accountContactRow.roles.push(contact.serviceTitle);
          }
        });

        if (accountContactRow.roles.length != 0) {
          accountContactRow.contacts = [contacts[0]];
          accountContactRows.push(accountContactRow);
        }
      } else if (contacts.length == 1) {
        //A Contact has only 1 role, check if that role has 1 contact, if so, make row
        let len = roleToContacts.get(contacts[0].serviceTitle)?.length;

        if (len && len == 1) {
          accountContactRow.contacts = [contacts[0]];
          accountContactRow.roles = [contacts[0].serviceTitle];
          accountContactRows.push(accountContactRow);
        }
      }
    }

    // 1 role to multiple contacts
    for (const [role, contacts] of roleToContacts.entries()) {
      let accountTableTuple = new AccountContactRow();

      //Check if a role has more than 1 contact
      if (contacts.length > 1) {
        contacts.forEach((contact) => {
          accountTableTuple.contacts.push(contact);
        });

        accountTableTuple.roles = [role];
        accountContactRows.push(accountTableTuple);
      }
    }

    return {
      programID: programID,
      programName: programName,
      contacts: sortAccountContactRows(accountContactRows),
      expanded: false,
    };
  });

  programContactsMap.value = programContacts.value.reduce((acc, pc) => {
    acc.set(pc.programID, pc);
    return acc;
  }, new Map<number, ProgramContact>());

  programSummary.value = programSummaryResult.map((p) => ({
    ...p,
    accountContact: programContactsMap.value.get(p.programId),
  }));

  loading.value = false;

  function sortAccountContactRows(rows: AccountContactRow[]): AccountContactRow[] {
    const sortedRows = rows.sort((a, b) => {
      const roleA = a.roles?.[0];
      const roleB = b.roles?.[0];
      if (roleA < roleB) {
        return -1;
      }
      if (roleA > roleB) {
        return 1;
      }
      return 0;
    });

    let topTwoRows: AccountContactRow[] = [];

    for (const row of sortedRows) {
      if ((row.roles[0] ?? '').match(/^Client Lead.*/)) {
        topTwoRows = [row, ...topTwoRows];
      }
      if (row.roles.includes('Account Executive')) {
        topTwoRows.push(row);
      }
    }

    return [...topTwoRows, ...sortedRows.filter((r) => !r.roles.includes('Account Executive') && !(r.roles[0] ?? '').match(/^Client Lead.*/))];
  }
}

function toggleShowAllExpirations(): void {
  showAllExpirations.value = !showAllExpirations.value;
  expirationsByMonth.value = showAllExpirations.value ? expirationsByMonthFull.value : expirationsByMonthShort.value;
}

function toggleProgramContactsExpand(program: ProgramContact | undefined): void {
  if (!program) return;
  program.expanded = !program.expanded ? true : !program.expanded;
  program.contacts.forEach((c) => {
    c.expanded = !c.expanded;
  });
}

function programLimit(program: ProgramSummary): string {
  if (program.hasMultipleCoverageTypes) {
    return 'Multiple';
  }
  return `${compactMoney(program.aggregateLimit)} / ${compactMoney(program.occurrenceLimit)}`;
}

function isProgramDetailsSectionExpanded(program: ProgramSummary): boolean {
  return program.accountContact?.expanded ?? false;
}

function formatProgramName(programName: string): string {
  return programName.length > MAX_PROGRAM_NAME_LENGTH ? programName.slice(0, MAX_PROGRAM_NAME_LENGTH - 1) + '...' : programName;
}

function emitSort(e: Event) {
  const target = e.target as HTMLElement;
  const { dataset } = target;
  const { sortkey } = dataset;

  if (sortkey) {
    let direction: 'ascending' | 'descending' = 'ascending';

    if (programDetailsSort.value) {
      if (programDetailsSort.value.direction === 'ascending') {
        direction = 'descending';
      } else {
        direction = 'ascending';
      }
    }

    programSummary.value.sort((a, b) => {
      const aVal = a[sortkey as keyof ProgramSummary] as number | undefined;
      const bVal = b[sortkey as keyof ProgramSummary] as number | undefined;

      if (aVal === undefined && bVal === undefined) return 0;
      if (aVal === undefined) return direction === 'ascending' ? -1 : 0;
      if (bVal === undefined) return direction === 'ascending' ? -1 : 0;

      return direction === 'ascending' ? Number(aVal) - Number(bVal) : Number(bVal) - Number(aVal);
    });

    programDetailsSort.value = {
      key: sortkey,
      direction: direction,
    };
  }
}

function className(key: string): any {
  if (!programDetailsSort.value || programDetailsSort.value.key !== key) return null;

  return {
    sorted: true,
    [programDetailsSort.value.direction]: true,
  };
}
</script>

<template>
  <div>
    <FilterBar class="filter-bar">
      <div class="d-flex filter-menu">
        <div class="filter-options ml-auto tw:gap-2">
          <toggle
            id="show-non-recurring"
            v-model="showNonRecurring"
            label="Show Non-Recurring Policies" />
          <toggle
            id="show-external"
            v-model="showExternal"
            :label="showExternalLabel" />
        </div>
      </div>
    </FilterBar>

    <PageProgress v-if="loading" />

    <div
      v-if="!loading"
      class="inner">
      <!-- charts -->
      <div class="charts">
        <div
          class="tw:flex tw:flex-col tw:justify-start tw:px-4"
          style="min-width: 400px">
          <!-- Doughnut Charts -->

          <h3 class="tw:mx-auto tw:my-0">
            {{ selectedDoughnutChart === 0 ? 'Premium by Coverage' : 'Premium by Carrier' }}
          </h3>
          <div
            data-test="doughnutCharts"
            class="flex-grow-0">
            <fusioncharts
              type="doughnut2d"
              width="100%"
              height="400"
              dataFormat="json"
              :dataSource="selectedDoughnutChart === 0 ? premiumByCoverageType : premiumByCarrier" />
          </div>

          <div
            class="tw:join tw:justify-center"
            data-testid="premium-chart-options">
            <input
              type="radio"
              name="premiumChartOptions"
              aria-label="Coverage"
              class="tw:btn tw:join-item tw:btn-sm"
              @click="selectedDoughnutChart = 0"
              :checked="selectedDoughnutChart === 0" />

            <input
              type="radio"
              name="premiumChartOptions"
              aria-label="Carrier"
              class="tw:btn tw:join-item tw:btn-sm"
              @click="selectedDoughnutChart = 1"
              :checked="selectedDoughnutChart === 1" />
          </div>
        </div>

        <div
          class="tw:flex tw:flex-col tw:px-4"
          style="min-width: 400px">
          <!-- Bar Charts -->

          <h3 class="tw:mx-auto tw:my-0">
            {{ selectedBarChart === 0 ? 'Top 10 Max Limits by Coverage' : 'Top 10 Deductibles/Retentions by Coverage' }}
          </h3>
          <div
            data-testid="barCharts"
            class="flex-grow-0">
            <fusioncharts
              type="bar2d"
              width="100%"
              height="400"
              dataFormat="json"
              :dataSource="selectedBarChart === 0 ? limitByCoverageType : retentionByCoverageType" />
          </div>

          <div
            class="tw:join tw:justify-center"
            data-testid="bar-chart-options">
            <input
              type="radio"
              name="barChartOptions"
              aria-label="Limits"
              class="tw:btn tw:join-item tw:btn-sm"
              @click="selectedBarChart = 0"
              :checked="selectedBarChart === 0" />

            <input
              type="radio"
              name="barChartOptions"
              aria-label="Deductibles/Retentions"
              class="tw:btn tw:join-item tw:btn-sm"
              @click="selectedBarChart = 1"
              :checked="selectedBarChart === 1" />
          </div>
        </div>

        <div class="tw:flex tw:flex-col">
          <!-- Program Summary -->
          <template v-if="(programSummary || []).length > 0">
            <h3 class="tw:mx-auto tw:mb-2">Program Details</h3>
            <div class="program-summary">
              <table class="apptable dense sortable">
                <thead>
                  <tr
                    ref="programDetailsHeaderRef"
                    @click="emitSort">
                    <td class="nosort tw:w-8 tw:rounded-tl-md tw:rounded-bl-md tw:p-2">&nbsp;</td>
                    <td class="nosort tw:w-36 tw:p-2 tw:text-center tw:align-middle">Program</td>
                    <td class="nosort tw:w-32 tw:p-2 tw:text-center tw:align-middle">Limit (Agg&nbsp;/&nbsp;Occ)</td>
                    <td
                      data-sortkey="retention"
                      class="tw:w-24 tw:p-2 tw:text-center tw:align-middle"
                      :class="className('retention')">
                      Retention
                    </td>
                    <td
                      data-sortkey="premium"
                      class="tw:w-24 tw:rounded-tr-md tw:rounded-br-md tw:p-2 tw:text-center tw:align-middle"
                      :class="className('premium')">
                      Premium
                    </td>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="program in programSummary"
                    :key="program.programId"
                    class="program-row">
                    <td
                      class="tw:cursor-pointer"
                      :class="{ 'tw:pt-3 tw:align-top': isProgramDetailsSectionExpanded(program) }"
                      @click.stop="toggleProgramContactsExpand(program.accountContact)"
                      data-testid="program-details-expand">
                      <div class="tw:h-fit-content tw:pl-2">
                        <PlusIcon
                          :size="14"
                          class="tw:stroke-secondary"
                          v-if="program.accountContact && !program.accountContact.expanded" />
                        <MinusIcon
                          :size="14"
                          class="tw:stroke-secondary"
                          v-if="program.accountContact && program.accountContact.expanded" />
                      </div>
                    </td>
                    <td class="tw:align-middle">
                      <div
                        class="cal-month-program"
                        :title="program.programName.length > MAX_PROGRAM_NAME_LENGTH ? program.programName : ''">
                        <component
                          :is="canAccessPrograms ? 'router-link' : 'span'"
                          :to="`programs/${program.programId}`">
                          <div style="text-align: center; font-size: 0.8rem">{{ formatProgramName(program.programName) }}</div>
                        </component>
                      </div>
                      <div
                        v-if="program.accountContact && program.accountContact.expanded"
                        class="tw:mt-2 tw:ml-1.5">
                        <AccountProgramContact
                          :program-contact="program.accountContact"
                          :expand="program.accountContact.expanded || false" />
                      </div>
                    </td>
                    <td class="tw:pt-2 tw:text-center tw:align-top">{{ programLimit(program) }}</td>
                    <td class="tw:pt-2 tw:text-center tw:align-top">{{ compactMoney(program.retention) }}</td>
                    <td class="tw:pt-2 tw:text-center tw:align-top">{{ compactMoney(program.premium) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </template>
        </div>
      </div>

      <div
        v-if="maxNumProgramsInMonth !== 0"
        class="tw:mx-auto tw:mt-8 tw:mb-8 tw:max-w-(--breakpoint-2xl)">
        <h3>Upcoming Expirations</h3>
        <div class="tw:mx-4 tw:mt-4 tw:mb-0 tw:grid tw:grid-cols-12 tw:rounded-md tw:border tw:border-solid tw:border-gray-300 tw:text-center">
          <div
            v-for="(month, index) in expirationsByMonth"
            :key="month.key"
            class="tw:grid tw:auto-rows-min tw:grid-cols-1 tw:border-0 tw:border-solid tw:border-gray-300"
            :class="{ 'tw:border-r-1': index < expirationsByMonth.length - 1 }">
            <div class="tw:border-0 tw:border-b-1 tw:border-solid tw:border-gray-300 tw:p-1 tw:text-xs tw:font-bold tw:text-gray-500 tw:uppercase">
              {{ month.label[0] }}
            </div>

            <div class="tw:p-1">
              <template v-for="(program, i) in month.programs">
                <div
                  v-if="i < NUM_PROGS_TO_SHOW || showAllExpirations"
                  class="cal-month-program"
                  :key="`${program.id}-${i}`">
                  <component
                    :is="canAccessPrograms ? 'router-link' : 'span'"
                    :to="`programs/${program.id}`">
                    <div class="tw:overflow-hidden tw:text-xs tw:font-bold tw:text-ellipsis tw:whitespace-nowrap">
                      {{ program.name }}
                    </div>
                  </component>
                </div>
                <div
                  v-else
                  :key="program.id"
                  class="tw:mt-1.5 tw:text-xs tw:font-bold tw:text-gray-500">
                  {{ program.name }}...
                </div>
              </template>
            </div>
          </div>
        </div>

        <div
          v-if="maxNumProgramsInMonth > NUM_PROGS_TO_SHOW"
          class="tw:mx-auto tw:mb-8 tw:max-w-min tw:cursor-pointer tw:rounded-b-md tw:border tw:border-t-0 tw:border-solid tw:border-gray-300 tw:bg-gray-100 tw:pt-1 tw:pr-3 tw:pb-0.5 tw:pl-2 tw:text-xs tw:font-bold tw:whitespace-nowrap tw:text-gray-500 tw:uppercase"
          @click="toggleShowAllExpirations">
          <div class="tw:flex tw:items-center">
            <ChevronUp :size="16" v-if="showAllExpirations" />
            <ChevronDown :size="16" v-else  />
            Show {{ showAllExpirations ? 'Less' : 'More' }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.charts {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  grid-gap: 12px;
  justify-content: center;
}

@media (max-width: 1250px) {
  .charts {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 400px) {
  .charts {
    grid-template-columns: 1fr;
  }
}

table {
  border-collapse: collapse;
}

.filter-menu {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.filter-options {
  display: flex;
  justify-content: flex-end;
}

.cal-month-program div {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding: 0.1rem 0.3rem;
  margin: 0.2rem;

  font-size: 0.7rem;
  font-weight: bold;
  border: 1px solid var(--chip-color);
  border-radius: 12px;
  color: var(--chip-color);
}

.cal-month-program a {
  text-decoration: none;
  color: var(--chip-color);
}

.program-summary {
  max-height: 500px;
  overflow-y: auto;
}

.program-summary td {
  padding: 0.2rem;
}
</style>
